import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import type {
  AuthTokens,
  User,
  Letter,
  LetterUpload,
  LetterUploadResponse,
  HealthStatus,
  PaginatedResponse,
} from '@/types'

const API_BASE_URL =
  (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_URL || '/api'

// Extend the InternalAxiosRequestConfig type to include _retry
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Create a separate instance for health checks (no /api prefix)
export const healthApi = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const refreshToken = localStorage.getItem('refresh_token')
        if (refreshToken) {
          const response = await authApi.refresh(refreshToken)
          localStorage.setItem('access_token', response.access_token)
          localStorage.setItem('refresh_token', response.refresh_token)

          originalRequest.headers.Authorization = `Bearer ${response.access_token}`
          return api(originalRequest)
        }
      } catch (_refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

// Auth API
export const authApi = {
  async signup(data: {
    username: string
    password: string
    full_name: string
    dob: string
    gender: string
    phone?: string
    address?: string
    referral_code: string
  }): Promise<User> {
    const response = await api.post('/auth/signup', data)
    return response.data
  },

  async login(username: string, password: string): Promise<AuthTokens> {
    const formData = new FormData()
    formData.append('username', username)
    formData.append('password', password)

    const response = await api.post('/auth/login', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  async refresh(refreshToken: string): Promise<AuthTokens> {
    const response = await api.post('/auth/refresh', { refresh_token: refreshToken })
    return response.data
  },

  async logout(): Promise<void> {
    await api.post('/auth/logout')
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  },

  async getMe(): Promise<User> {
    const response = await api.get('/auth/me')
    return response.data
  },

  async getReferralInfo(): Promise<{
    total_referrals: number
    active_codes: number
    today_code: string
    today_code_expires_at: string
    total_codes_created: number
  }> {
    const response = await api.get('/auth/me/referral')
    return response.data
  },
}

// Letters API
export const lettersApi = {
  async list(params?: {
    page?: number
    page_size?: number
    limit?: number
    search?: string
    tags?: string[]
    is_published?: boolean
    is_public?: boolean
    tag?: string
  }): Promise<PaginatedResponse<Letter>> {
    // Map frontend param names to backend expected names
    const backendParams: Record<string, unknown> = {}
    if (params?.page) backendParams.page = params.page
    if (params?.page_size) backendParams.limit = params.page_size
    if (params?.limit) backendParams.limit = params.limit
    if (params?.tag) backendParams.tag = params.tag
    if (params?.is_public !== undefined) backendParams.is_public = params.is_public
    if (params?.is_published !== undefined) backendParams.is_public = params.is_published

    const response = await api.get('/letters/', { params: backendParams })
    return response.data
  },

  async get(id: string): Promise<Letter> {
    const response = await api.get(`/letters/${id}`)
    return response.data
  },

  async create(data: LetterUpload): Promise<Letter | LetterUploadResponse> {
    const formData = new FormData()
    formData.append('title', data.title)

    // File is required for upload endpoint
    if (data.file) {
      formData.append('file', data.file)
    } else if (data.content) {
      // If no file, create a text file from content
      const blob = new Blob([data.content], { type: 'text/plain' })
      formData.append('file', blob, 'letter.txt')
    }

    // Backend expects these field names
    if (data.sender_info) formData.append('sender', data.sender_info.name || '')
    if (data.recipient_info) formData.append('recipient', data.recipient_info.name || '')
    if (data.tags) formData.append('tags', data.tags.join(','))
    if (data.is_published !== undefined) formData.append('is_public', String(data.is_published))

    // Add date if provided
    if (data.date_written) formData.append('date_written', data.date_written)

    const response = await api.post('/letters/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  async update(id: string, data: Partial<LetterUpload>): Promise<Letter> {
    const response = await api.put(`/letters/${id}`, data)
    return response.data
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/letters/${id}`)
  },

  // Re-enable download functionality
  async download(id: string): Promise<Blob> {
    const response = await api.get(`/letters/${id}/download`, {
      responseType: 'blob',
    })
    return response.data
  },
}

// Health Check API
export const healthCheckApi = {
  async check(): Promise<HealthStatus> {
    // Health endpoint is at /api/ (using healthApi instance for consistency)
    const response = await healthApi.get('/api/')
    return response.data
  },
}

// Public API (no auth required)
export const publicApi = {
  async getContact(): Promise<unknown> {
    // Contact endpoint is at root level (no /api prefix)
    const response = await axios.get(`${API_BASE_URL}/contact`)
    return response.data
  },

  async getDocs(): Promise<unknown> {
    const response = await api.get('/v1/docs/api/navigation')
    return response.data
  },
}
